import { useRef, useState } from 'react';
import {
  type DiscoverNoopReason,
  type ImportResult,
  type WatchlistDeriveSummary,
  discoverWatchlistSource,
  importCorpusText,
  rederiveWatchlist,
} from '../lib/api';
import type { OnboardingStep } from '../lib/onboardingStep';
import { ThemeSwatches } from '../theme/ThemeSwatches';
import { VoiceTextarea } from './VoiceTextarea';
import styles from './Onboarding.module.css';

interface OnboardingProps {
  /** The resolved step from useOnboardingGate — corpus-state -> step, per
   *  docs/diagrams/c3-onboarding-2026-07-04.svg. 'done' means this component
   *  renders nothing (App.tsx shows the tab bar instead). */
  step: OnboardingStep;
  /** Advance local progress to (at least) this step — see onboardingStep.ts's
   *  advanceOnboardingProgress; never regresses. */
  onAdvance: (step: OnboardingStep) => void;
  /** The explicit skip affordance — retires the overlay outright. */
  onSkip: () => void;
  /** Navigate into the Refine tab (step 3's "build the rest" hand-off) or the
   *  Profiles/Get Found tab (step 4's "first real output" CTA) — App.tsx owns
   *  routing, this component only asks for it. */
  onNavigate: (path: '/refine' | '/profiles') => void;
  /** Whether the corpus has at least one real role yet (server truth,
   *  useOnboardingGate's corpus.roleCount > 0) — gates step 4's Generate CTA
   *  exactly like the diagram's "fires once >=1 role exists (server truth,
   *  not a client guess)" annotation. */
  hasRealCorpus: boolean;
}

const STEP_DOT_ORDER: OnboardingStep[] = [
  'demo',
  'one-question',
  'interview',
  'first-output',
  'watchlist-seed',
];

function Dot({ active, filled }: { active: boolean; filled: boolean }) {
  const size = active ? styles.dotActive : styles.dotInactive;
  const fill = filled ? styles.dotFilled : styles.dotEmpty;
  return <span className={`${styles.dot} ${size} ${fill}`} />;
}

/**
 * The onboarding overlay — REWORKED to docs/diagrams/c3-onboarding-2026-07-04.svg's
 * 5 nodes (this file previously held B-1's placeholder 3-step welcome/upload/
 * handoff shell; that upload step was an explicit LOCAL setInterval demo, not
 * a real import — see the prior version's own header, which named this exact
 * rework as "NOT this brief" for B-1). Every step below calls a REAL, existing
 * backend route; nothing here is a new endpoint (mission law: no backend
 * changes).
 *
 * Step 0 (landing + Google OAuth sign-in) lives OUTSIDE this component, in
 * App.tsx — it is a routing/auth concern, not an overlay-content concern, and
 * a signed-out stranger never reaches this component at all (App.tsx renders
 * the sign-in screen instead — see that file's own comment).
 */
export function Onboarding({ step, onAdvance, onSkip, onNavigate, hasRealCorpus }: OnboardingProps) {
  if (step === 'done') return null;

  const dotIndex = STEP_DOT_ORDER.indexOf(step);

  return (
    <div className={styles.overlay}>
      <div className={styles.topRow}>
        <div className={styles.dots}>
          {STEP_DOT_ORDER.map((s, i) => (
            <Dot key={s} active={i === dotIndex} filled={dotIndex >= 0 && i <= dotIndex} />
          ))}
        </div>
        <button type="button" className={styles.skipButton} onClick={onSkip}>
          Skip
        </button>
      </div>

      {step === 'demo' && <DemoStep onNext={() => onAdvance('one-question')} />}

      {step === 'one-question' && (
        <OneQuestionStep
          onImported={() => onAdvance('interview')}
          onSkip={() => onAdvance('interview')}
        />
      )}

      {step === 'interview' && (
        <InterviewStep
          onOpenRefine={() => {
            onAdvance('first-output');
            onNavigate('/refine');
          }}
          onSkip={() => onAdvance('first-output')}
        />
      )}

      {step === 'first-output' && (
        <FirstOutputStep
          hasRealCorpus={hasRealCorpus}
          onGenerate={() => {
            onAdvance('watchlist-seed');
            onNavigate('/profiles');
          }}
          onSkip={() => onAdvance('watchlist-seed')}
        />
      )}

      {step === 'watchlist-seed' && <WatchlistSeedStep onDone={() => onAdvance('done')} />}
    </div>
  );
}

// =============================================================================
// STEP 1 · LABELED DEMO CARD (diagram node 1) — pre-signup, no mic, ZERO
// backend call. Static sample data ONLY, with the persistent "DEMO — not your
// data" band the NO-INVENTION law requires whenever a screen shows a shape of
// output before any real corpus exists. Mirrors the diagram's own honesty
// note verbatim.
// =============================================================================

const DEMO_SAMPLE = {
  title: 'Platform Lead',
  company: 'Bright Labs',
  why: 'Your cross-functional delivery work and your knack for turning ambiguity into a working system — the kind of story recruiters skim past on a plain resume, but light up for once it is told straight.',
};

function DemoStep({ onNext }: { onNext: () => void }) {
  return (
    <>
      <div className={styles.stepCentered}>
        <div className={styles.welcomeAvatar}>M</div>
        <div className={styles.welcomeTitle}>Hi, I'm Meira.</div>
        <div className={styles.welcomeBody}>
          Here's the shape of what I can find in your story — using a sample, not yours yet.
        </div>

        <div className={styles.demoCard}>
          <div className={styles.demoBand}>DEMO — not your data</div>
          <div className={styles.demoTitle}>{DEMO_SAMPLE.title}</div>
          <div className={styles.demoCompany}>{DEMO_SAMPLE.company}</div>
          <div className={styles.demoWhyLabel}>Why this fits</div>
          <div className={styles.demoWhy}>{DEMO_SAMPLE.why}</div>
        </div>
      </div>
      <button type="button" className={styles.primaryButton} onClick={onNext}>
        Show me what's true about me
      </button>
      <div className={styles.swatchRow}>
        <ThemeSwatches />
      </div>
    </>
  );
}

// =============================================================================
// STEP 2 · ONE QUESTION, VOICE OR TEXT (diagram node 2) — mic and text box
// side-by-side, neither gated; the mic permission prompt is entirely
// browser-native (VoiceTextarea -> useDictation -> SpeechRecognition.start()),
// fired only when the mic button is actually tapped, never at mount. Submits
// to the REAL POST /corpus/import (source:'paste') — the SAME route
// ImportPasteCard already calls on the Refine tab, so the first real
// achievement this stranger creates lands in the SAME corpus their later
// Refine-tab imports build on, not a separate onboarding-only bucket.
// =============================================================================

const MIN_ANSWER_LENGTH = 20;

function OneQuestionStep({
  onImported,
  onSkip,
}: {
  onImported: (result: ImportResult) => void;
  onSkip: () => void;
}) {
  const [answer, setAnswer] = useState('');
  const [phase, setPhase] = useState<'idle' | 'reading' | 'done' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  const tooShort = answer.trim().length > 0 && answer.trim().length < MIN_ANSWER_LENGTH;
  const canSubmit = answer.trim().length >= MIN_ANSWER_LENGTH && phase !== 'reading';

  async function handleSubmit() {
    if (!canSubmit) return;
    setPhase('reading');
    setErrorMessage(null);
    try {
      const imported = await importCorpusText(answer.trim(), 'paste');
      setResult(imported);
      setPhase('done');
    } catch (err) {
      setPhase('error');
      setErrorMessage(err instanceof Error ? err.message : 'Could not save that — try again.');
    }
  }

  return (
    <div className={styles.stepColumn}>
      <div className={styles.uploadTitle}>One piece of work you're proud of.</div>
      <div className={styles.uploadBody}>
        Speak it or type it — whichever feels easier. Even a small one — what comes to mind?
      </div>

      {phase !== 'done' && (
        <>
          <VoiceTextarea
            label="Your answer"
            value={answer}
            onChange={setAnswer}
            placeholder="A project, a fix, a moment someone thanked you for…"
            rows={5}
            disabled={phase === 'reading'}
            hint={tooShort ? `${MIN_ANSWER_LENGTH - answer.trim().length} more characters` : undefined}
          />
          <button
            type="button"
            className={styles.primaryButton}
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {phase === 'reading' ? 'Reading it…' : 'That\'s the one'}
          </button>
          <button type="button" className={styles.textSkipButton} onClick={onSkip}>
            I'll come back to this
          </button>
          {phase === 'error' && errorMessage ? (
            <div className={styles.oneQErrorRow} role="alert">
              {errorMessage}
            </div>
          ) : null}
        </>
      )}

      {phase === 'done' && result ? (
        <div className={styles.doneWrap}>
          <div className={styles.doneHeaderRow}>
            <div className={styles.doneCheck}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M5 13l4 4L19 7"
                  stroke="currentColor"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className={styles.doneHeaderText}>That's real, from your words.</div>
          </div>
          <div className={styles.doneNote}>{describeOneQuestionResult(result)}</div>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() => onImported(result)}
          >
            Continue
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** Honest, count-only summary of what the server actually reported — same
 *  discipline as ImportPasteCard's describeResult, never a guessed count. */
function describeOneQuestionResult(result: ImportResult): string {
  const parts: string[] = [];
  if (typeof result.rolesCreated === 'number' && result.rolesCreated > 0) {
    parts.push(`${result.rolesCreated} role${result.rolesCreated === 1 ? '' : 's'}`);
  }
  if (typeof result.achievementsCreated === 'number' && result.achievementsCreated > 0) {
    parts.push(`${result.achievementsCreated} ${result.achievementsCreated === 1 ? 'story' : 'stories'}`);
  }
  if (parts.length === 0) {
    return "Saved — I found the thread, even if it didn't need a new role or story card.";
  }
  return `I found ${parts.join(' and ')} in that.`;
}

// =============================================================================
// STEP 3 · BUILD THE REST (diagram node 3) — skippable, resumable hand-off
// into the Refine tab's real orb + corpus canvas. This step does NOT
// duplicate the voice session or the canvas here (both already exist, fully
// wired, on Refine.tsx) — it hands off, per the mission's "link, don't
// re-implement" instruction for the presence flow, applied the same way here.
// =============================================================================

function InterviewStep({ onOpenRefine, onSkip }: { onOpenRefine: () => void; onSkip: () => void }) {
  return (
    <>
      <div className={styles.stepCentered}>
        <div className={styles.handoffTitle}>That's one thread — let's pull the others.</div>
        <div className={styles.handoffBody}>
          I'll keep asking, gently. Answer as many as you like, skip what doesn't fit today, and pick
          up again whenever you're ready — nothing here is timed or one-shot.
        </div>
        <div className={styles.handoffPreview}>
          <div className={styles.handoffAvatar}>M</div>
          <div className={styles.handoffBubble}>
            Tell me about a moment at work you're quietly proud of…
          </div>
        </div>
      </div>
      <button type="button" className={styles.primaryButton} onClick={onOpenRefine}>
        Keep going with Meira
      </button>
      <button type="button" className={styles.textSkipButton} onClick={onSkip}>
        Not now — take me onward
      </button>
    </>
  );
}

// =============================================================================
// STEP 4 · FIRST REAL OUTPUT (diagram node 4) — the demo card is explicitly
// contrasted and named as retired here (never just hidden); the Generate CTA
// LINKS to the real Get Found/Profiles tab (POST /presence/generate lives
// there, gated on platform + targetRoleFamily inputs that tab already owns —
// re-implementing that call here would duplicate real logic the mission's
// "link, don't re-implement" law explicitly forbids). Gated on hasRealCorpus
// (server truth, useOnboardingGate's corpus.roleCount > 0) — never a client
// guess, matching the diagram's own annotation.
// =============================================================================

function FirstOutputStep({
  hasRealCorpus,
  onGenerate,
  onSkip,
}: {
  hasRealCorpus: boolean;
  onGenerate: () => void;
  onSkip: () => void;
}) {
  return (
    <div className={styles.stepColumn}>
      <div className={styles.uploadTitle}>Time for the real thing.</div>
      <div className={styles.uploadBody}>
        That sample from the start? It's retired now — what you build next is actually yours.
      </div>

      <div className={styles.retiredDemoRow}>
        <span className={styles.retiredBadge}>RETIRED — the sample</span>
        <span className={styles.retiredLabel}>{DEMO_SAMPLE.title} · {DEMO_SAMPLE.company}</span>
      </div>

      {hasRealCorpus ? (
        <button type="button" className={styles.primaryButton} onClick={onGenerate}>
          Generate my real Get Found profile
        </button>
      ) : (
        <div className={styles.waitingNote}>
          I need at least one real story before I can generate something true about you — head back
          and answer one question first.
        </div>
      )}

      <button type="button" className={styles.textSkipButton} onClick={onSkip}>
        Skip for now
      </button>
    </div>
  );
}

// =============================================================================
// STEP 5 · THE WATCHLIST SEED — "she keeps looking" (diagram node 5).
// Rederive from Get Found titles + an honest discover-by-URL affordance +
// outreach OFFERED, never auto-sent (matches the diagram's outreach note
// exactly — the actual draft compose lives on the Track tab, per api.ts's
// OUTREACH module; this step only confirms the loop is seeded, it does not
// compose a draft itself since no specific job is in view yet here).
// =============================================================================

function WatchlistSeedStep({ onDone }: { onDone: () => void }) {
  const [rederivePhase, setRederivePhase] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [rederiveSummary, setRederiveSummary] = useState<WatchlistDeriveSummary | null>(null);
  const [rederiveError, setRederiveError] = useState<string | null>(null);

  const [careersUrl, setCareersUrl] = useState('');
  const [discoverPhase, setDiscoverPhase] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [discoverMessage, setDiscoverMessage] = useState<string | null>(null);
  // QA #14 (the UX half — his recorded walkthrough ask): a genuinely-new add
  // gets a LOUD confirmation (the doneCheck pattern), the input clears (a
  // still-populated box read as "nothing happened" on the live walk), and an
  // "add another" affordance appears. Everything else keeps the quiet row.
  const [discoverJustAdded, setDiscoverJustAdded] = useState(false);
  const discoverInputRef = useRef<HTMLInputElement | null>(null);

  async function handleRederive() {
    setRederivePhase('running');
    setRederiveError(null);
    try {
      const { ok: _ok, ...summary } = await rederiveWatchlist();
      setRederiveSummary(summary);
      setRederivePhase('done');
    } catch (err) {
      setRederivePhase('error');
      setRederiveError(err instanceof Error ? err.message : 'Could not seed your watchlist — try again.');
    }
  }

  async function handleDiscover() {
    const url = careersUrl.trim();
    if (!url) return;
    setDiscoverPhase('running');
    setDiscoverMessage(null);
    try {
      const result = await discoverWatchlistSource(url);
      const added = isDiscoverAdd(
        result.matches.length,
        result.created.length,
        result.reason ?? null,
      );
      setDiscoverPhase('done');
      setDiscoverMessage(
        describeDiscoverResult(result.matches.length, result.created.length, result.reason ?? null),
      );
      setDiscoverJustAdded(added);
      if (added) setCareersUrl('');
    } catch (err) {
      setDiscoverPhase('error');
      setDiscoverJustAdded(false);
      setDiscoverMessage(err instanceof Error ? err.message : 'Could not check that URL — try again.');
    }
  }

  return (
    <div className={styles.stepColumn}>
      <div className={styles.uploadTitle}>Now I keep looking.</div>
      <div className={styles.uploadBody}>
        Even after you close this tab, I'll keep watching for roles that fit — you'll get outreach
        offered, never sent without you.
      </div>

      <div className={styles.seedCard}>
        <div className={styles.seedCardTitle}>Seed from your declared roles</div>
        <div className={styles.seedCardBody}>
          Pull search terms from what you've told Get Found you want to be found for.
        </div>
        <button
          type="button"
          className={styles.seedButton}
          onClick={handleRederive}
          disabled={rederivePhase === 'running'}
        >
          {rederivePhase === 'running' ? 'Seeding…' : 'Seed my watchlist'}
        </button>
        {rederivePhase === 'done' && rederiveSummary ? (
          <RederiveResultBlock summary={rederiveSummary} />
        ) : null}
        {rederivePhase === 'error' && rederiveError ? (
          <div className={styles.seedErrorRow} role="alert">
            {rederiveError}
          </div>
        ) : null}
      </div>

      <div className={styles.seedCard}>
        <div className={styles.seedCardTitle}>Or point me at a company</div>
        <div className={styles.seedCardBody}>
          Paste a company's own careers page — I'll check if I can watch it directly.
        </div>
        <div className={styles.discoverRow}>
          <input
            ref={discoverInputRef}
            type="url"
            className={styles.discoverInput}
            value={careersUrl}
            onChange={(e) => setCareersUrl(e.target.value)}
            placeholder="https://company.com/careers"
            disabled={discoverPhase === 'running'}
          />
          <button
            type="button"
            className={styles.discoverButton}
            onClick={handleDiscover}
            disabled={discoverPhase === 'running' || !careersUrl.trim()}
          >
            {discoverPhase === 'running' ? 'Checking…' : 'Check'}
          </button>
        </div>
        {discoverMessage && discoverJustAdded && discoverPhase === 'done' ? (
          <div className={styles.discoverDoneWrap} role="status">
            <div className={styles.discoverDoneRow}>
              <div className={styles.doneCheck} aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M5 13l4 4L19 7"
                    stroke="currentColor"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className={styles.discoverDoneText}>{discoverMessage}</div>
            </div>
            <button
              type="button"
              className={styles.seedButton}
              onClick={() => {
                setDiscoverMessage(null);
                setDiscoverJustAdded(false);
                setDiscoverPhase('idle');
                discoverInputRef.current?.focus();
              }}
            >
              Add another company
            </button>
          </div>
        ) : discoverMessage ? (
          <div className={discoverPhase === 'error' ? styles.seedErrorRow : styles.seedResultRow} role="status">
            {discoverMessage}
          </div>
        ) : null}
      </div>

      <button type="button" className={styles.primaryButton} onClick={onDone}>
        Take me in
      </button>
    </div>
  );
}

/** The rendered result of a completed rederive pass — the headline message
 *  plus (when applicable) the current-total line underneath. Split out of
 *  the inline JSX above so each describe* helper is called exactly once per
 *  render, not twice (a conditional-render-then-render-again pattern would
 *  otherwise call the same pure function twice for one paint — harmless
 *  here since both are pure/side-effect-free, but wasteful and easy to let
 *  drift out of sync if either grows a branch later). */
function RederiveResultBlock({ summary }: { summary: WatchlistDeriveSummary }) {
  const totalLine = describeEnabledSearchTotal(summary);
  return (
    <div className={styles.seedResultRow} role="status">
      <div>{describeRederiveResult(summary)}</div>
      {totalLine ? <div className={styles.seedResultTotal}>{totalLine}</div> : null}
    </div>
  );
}

/**
 * QA #15 (Wave 2, lane B — docs/DEMO-WALKTHROUGH-issues.md row #15): the raw
 * created/reactivated counts alone confused Jason live ("14 new searches"
 * while the total quietly climbed 30→44→52, no explanation). Now that #8's
 * deterministic derive means a re-seed with no changes is a TRUE no-op (0
 * created, 0 reactivated, 0 disabled), the panel can finally tell the truth
 * plainly instead of dressing up churn: every branch below renders ONLY
 * fields this exact response actually carries — nothing here is a guess or a
 * client-side count. Checked in this order:
 *   1. blockedReason (QA Tier-2 #5's honest block — no active search
 *      scenario resolves a geography) — this can occur even WITH declared
 *      roles, so it is checked first, never masked by the roles branch below.
 *   2. no declared roles at all — nothing to seed from yet.
 *   3. true no-op — created/reactivated/disabledStale all zero.
 *   4. real change — human-worded counts for exactly what happened, with
 *      "retired" (not the internal "disabledStale") as the human word for a
 *      row this pass turned off because its title is no longer declared.
 */
export function describeRederiveResult(summary: WatchlistDeriveSummary): string {
  if (summary.blockedReason) {
    return "Add a search location on Get Found first, then seed your watchlist — I need to know where to look before I can start.";
  }
  if (summary.desiredRoles.length === 0) {
    return "You haven't declared any roles on Get Found yet — do that first, then seed again.";
  }

  const createdCount = summary.created.length;
  const reactivatedCount = summary.reactivated.length;
  const retiredCount = summary.disabledStale.length;

  if (createdCount === 0 && reactivatedCount === 0 && retiredCount === 0) {
    return 'Your searches are up to date — nothing changed.';
  }

  const parts: string[] = [];
  if (createdCount > 0) {
    parts.push(`${createdCount} search${createdCount === 1 ? '' : 'es'} created (from your declared roles)`);
  }
  if (reactivatedCount > 0) {
    parts.push(`${reactivatedCount} search${reactivatedCount === 1 ? '' : 'es'} turned back on`);
  }
  if (retiredCount > 0) {
    parts.push(`${retiredCount} search${retiredCount === 1 ? '' : 'es'} retired (no longer one of your roles)`);
  }
  return `${joinHumanList(parts)}.`;
}

/** Oxford-comma-safe "A, B, and C" join for 1-3 human-worded parts — never a
 *  bare comma splice for exactly two parts (the awkward "A, B." shape). */
function joinHumanList(parts: string[]): string {
  if (parts.length <= 1) return parts.join('');
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

/** The current-total line, shown under the headline for every NON-blocked
 *  outcome (a block means nothing was touched — showing a stale total there
 *  would read as progress that didn't happen). `enabledSearchCount` is a
 *  served field (QA #15, additive on POST /watchlist/rederive) — this NEVER
 *  sums the other fields on the summary itself, since that would silently
 *  drift the moment the server's own tally differs (e.g. hand-made rows this
 *  pass never touches at all). Returns null (render nothing) when the field
 *  is absent — an older cached response shape — rather than showing a
 *  fabricated 0. */
export function describeEnabledSearchTotal(summary: WatchlistDeriveSummary): string | null {
  if (summary.blockedReason) return null;
  if (typeof summary.enabledSearchCount !== 'number') return null;
  const n = summary.enabledSearchCount;
  return `${n} search${n === 1 ? '' : 'es'} watching for you right now.`;
}

/** Honest, count-and-reason summary for the discover-by-URL result. `reason`
 *  (additive, 2026-07-04 bug-fix lane) is set by the server on every empty
 *  outcome (matchCount === 0 or createdCount === 0 with matchCount > 0) — no
 *  path here falls through to a generic message when the server told us
 *  exactly why. Absent `reason` (an older cached response shape, or a
 *  genuinely non-empty result) falls back to the pre-existing count-only
 *  copy so this never regresses for a server that hasn't redeployed yet.
 *
 *  Exported (QA #14 probe, 2026-07-04 — docs/DEMO-WALKTHROUGH-issues.md
 *  row #14 / docs/QA-RECOMMENDATIONS-2026-07-04.md's "live-repro first")
 *  purely so this pure function can be pinned directly by
 *  Onboarding.discoverResult.test.ts — no behavior change, no new caller.
 *  This repo has no DOM-rendering test harness (no jsdom/happy-dom/
 *  @testing-library anywhere in the monorepo, verified) — every existing
 *  React-adjacent test in this house (onboardingStep.test.ts) pins pure
 *  logic the same way; this follows that exact precedent rather than
 *  introducing new test infrastructure for one probe. */
export function describeDiscoverResult(
  matchCount: number,
  createdCount: number,
  reason: DiscoverNoopReason | null,
): string {
  if (matchCount === 0) {
    switch (reason) {
      case 'invalid_careers_url':
        return "That doesn't look like a valid URL — check it and try again.";
      case 'domain_claimed_today':
        return "I already checked that company today — try again tomorrow, or I'll pick it up on my own.";
      case 'fetch_failed':
        return "Couldn't reach that page just now — try again in a moment.";
      // 'already_ats_hosted_url' | 'no_ats_detected' | null (older server
      // shape) all fall through to the same honest default — nothing on
      // that page reads as an ATS board Mira can watch directly.
      default:
        return "I don't recognize anything on that page — I can't watch this one directly yet.";
    }
  }
  if (createdCount === 0) {
    // reason is 'already_watched' on this branch (matchCount > 0, createdCount
    // === 0) — the only no-op reason that can co-occur with a non-empty
    // matches array; no other reason reaches this branch by construction.
    return 'Recognized it — already on your watchlist.';
  }
  return `Found it — now watching ${createdCount} board${createdCount === 1 ? '' : 's'} there.`;
}

/** QA #14 (the UX half — his recorded walkthrough ask): TRUE only for a
 *  genuinely-new add — matches found, at least one source created, no
 *  reason. Drives the LOUD confirmation + clear-the-input + the "add
 *  another" affordance. Every other outcome (duplicate, no-op, blocked)
 *  keeps the quiet row — a no-op must never celebrate. Pure + exported for
 *  the same pinning-test convention as describeDiscoverResult above. */
export function isDiscoverAdd(
  matchCount: number,
  createdCount: number,
  reason: string | null,
): boolean {
  return reason === null && matchCount > 0 && createdCount > 0;
}
